import React, { MouseEventHandler, ReactNode, useState } from 'react'
import { Box, Button, ButtonProps } from '@mui/material'
import { ConfirmDialog } from 'shared/ui'

interface ConfirmWithDialogProps extends ButtonProps {
  dialogTitle: string
  dialogContent: ReactNode
  onClick(): void
  cta?: string
  cancel?: string
  canConfirm?: boolean
}

/**
 * Button that opens a confirmation dialog before executing the action.
 * @param dialogTitle - Confirmation dialog title
 * @param dialogContent - Dialog content node
 * @param onClick - Callback invoked after confirmation
 * @param cta - Confirm button label
 * @param cancel - Cancel button label
 * @param canConfirm - Optional guard to disable confirming
 */
export function ConfirmWithDialogButton({
  dialogTitle,
  dialogContent,
  onClick,
  canConfirm,
  ...props
}: ConfirmWithDialogProps) {
  const [showDialog, setShowDialog] = useState(false)
  const confirmed: MouseEventHandler = (e) => {
    e.stopPropagation()
    setShowDialog(false)
    onClick()
  }
  const closed: MouseEventHandler = (e) => {
    e.stopPropagation()
    setShowDialog(false)
  }
  const buttonClick: MouseEventHandler = (e) => {
    e.stopPropagation()
    setShowDialog(true)
  }
  return (
    <>
      <Button onClick={buttonClick} {...props} />
      <ConfirmDialog
        title={dialogTitle}
        onConfirm={confirmed}
        onClose={closed}
        open={showDialog}
        cta={props.cta || 'Yes'}
        cancel={props.cancel || 'No'}
        canConfirm={canConfirm}
      >
        {dialogContent}
      </ConfirmDialog>
    </>
  )
}

/**
 * Button that requires a second click to confirm an action inline.
 * @param onClick - Callback invoked after confirmation
 */
export function ConfirmCommitButton({
  onClick,
  children,
  ...props
}: ButtonProps) {
  const [confirming, setConfirming] = useState(false)

  const onConfirm = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setConfirming(false)
    if (onClick) {
      onClick(e)
    }
  }

  if (confirming) {
    return (
      <Box component={'span'} padding={'5px'} border={'solid 1px'}>
        <Button onClick={onConfirm}>Confirm</Button>
        <Button
          onClick={(e) => {
            e.stopPropagation()
            setConfirming(false)
          }}
        >
          Cancel
        </Button>
      </Box>
    )
  }
  return (
    <Button
      onClick={(e) => {
        e.stopPropagation()
        setConfirming(true)
      }}
      {...props}
    >
      {children}
    </Button>
  )
}
